import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import _ from 'lodash';
import { Controller, FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { t, Trans } from '@lingui/macro';
import { Box, CircularProgress, MenuItem, Typography } from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';

import useAlerts from '../../../stores/AlertsStore';
import { useDataService } from '../../../services/ApiDataService';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { useFind } from '../../../services/FormQueryService';
import { useFormSaveService, useFormSubmitWrapper } from '../../../services/FormSaveService';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import NgpSelect from '../../form/NgpSelect';
import NgpToggle from '../../form/NgpToggle';
import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import { RowFlexLayout } from '../../layout';
import {
    dirtyFormBodyText,
    dirtyFormCancelText,
    dirtyFormConfirmText,
    dirtyFormTitleText,
    useStyles as useDirtyPromptStyles,
} from '../../form/DirtyFormConfirm';
import { getTestTypes, getTestsByTypeIds, patchTestThresholdOverride, updateGlobalThresholds } from './ThresholdsApi';
import { handleThresholdSaveError } from './ThresholdsSaveError';
import { DEFAULT_SERVICE_GROUP, GROUP_TYPE_FIELD } from './ThresholdsConstants';
import ThresholdSensitivitySlider from './ThresholdSensitivitySlider';
import ThresholdSensitivityExampleChart from './ThresholdSensitivityExampleChart';
import ThresholdsServiceOverridesSelector from './ThresholdsServiceOverridesSelector';
import { useStyles } from './ThresholdsServiceTabStyles';
import { useHelpUrl } from '../../../utilities/HelpUtilities';
import ThresholdMetricRow from './ThresholdMetricRow';

const TEST_GROUP_TITLES = {
    VoIP: 'VoIP',
};
const OVERRIDE_TEST_IDS_FIELD = 'overrideTestIds';

function asNumber(value) {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
}

function buildServiceDefaults(testType) {
    return {
        thresholds: _.cloneDeep(testType?.thresholds || []),
        automaticEnabled: _.has(testType, 'automaticThreshold.sensitivity'),
        automaticSensitivity: _.get(testType, 'automaticThreshold.sensitivity', 4),
    };
}

function metricTitleFallback(metricName) {
    if (!metricName) {
        return '';
    }

    const normalized = metricName.replace(/_/g, ' ').trim();

    if (!normalized.includes(' ')) {
        return _.upperFirst(normalized);
    }

    return normalized.replace(/\w\S*/g, (word) => _.upperFirst(word.toLowerCase()));
}

function groupTitleFallback(groupName) {
    if (!groupName) {
        return '';
    }

    return TEST_GROUP_TITLES[groupName] || _.startCase(groupName.replace(/_/g, ' ')).trim();
}

function AutomaticThresholdControls({ classes, automaticEnabled }) {
    const autoThresholdsHelpUrl = useHelpUrl('AutomaticThresholds');
    const { control } = useFormContext();

    return (
        <RowFlexLayout vBottom mt={2} className={classes.automaticThresholdControlsRow}>
            <NgpToggle fieldName={'automaticEnabled'} label={<Trans>Use Automatic Thresholds</Trans>} />
            <a
                href={autoThresholdsHelpUrl}
                target="_blank"
                rel="noreferrer"
                title={t`View help documentation regarding automatic thresholds`}
                style={{ marginBottom: 5 }}
            >
                <HelpIcon fontSize="small" />
            </a>

            {!!automaticEnabled && (
                <Box ml={4}>
                    <div className={classes.sensitivityControls}>
                        <Controller
                            control={control}
                            name="automaticSensitivity"
                            render={({ value, onChange }) => (
                                <ThresholdSensitivitySlider
                                    value={_.clamp(asNumber(value) || 4, 1, 5)}
                                    onChange={(event, sliderValue) => {
                                        onChange(sliderValue);
                                    }}
                                    containerClassName={classes.sensitivitySliderRow}
                                    labelClassName={classes.sensitivityLabelInline}
                                    sliderWrapClassName={classes.sensitivitySliderWrap}
                                />
                            )}
                        />
                    </div>
                </Box>
            )}
        </RowFlexLayout>
    );
}

const ThresholdsServiceTab = forwardRef((props, ref) => {
    const { initialServiceGroup = '' } = props;
    const initialGroup = initialServiceGroup || DEFAULT_SERVICE_GROUP;

    const classes = useStyles();
    const dirtyPromptClasses = useDirtyPromptStyles();
    const dataService = useDataService();
    const [, alertsActions] = useAlerts();
    const { errorWrapper } = useErrorWrapper();
    const { submitWrapper, saving } = useFormSubmitWrapper();
    const formSaveService = useFormSaveService();

    const [serviceGroup, setServiceGroup] = useState(initialGroup);
    const [changeToGroup, setChangeToGroup] = useState(null);
    const [displayDirtyPrompt, setDisplayDirtyPrompt] = useState(false);
    const [isSubFormDirty, setIsSubFormDirty] = useState(false);
    const [findingGroupTests, setFindingGroupTests] = useState(false);
    const [allTestTypes, setAllTestTypes] = useState([]);
    const [tests, setTests] = useState([]);
    const [dirtyOverrides, setDirtyOverrides] = useState({});

    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        shouldUnregister: false,
        defaultValues: {
            [GROUP_TYPE_FIELD]: initialGroup,
            thresholds: [],
            automaticEnabled: false,
            automaticSensitivity: 4,
            [OVERRIDE_TEST_IDS_FIELD]: [],
        },
        mode: 'onChange',
    });
    const {
        reset,
        setError,
        setValue,
        getValues,
        watch,
        control,
        handleSubmit,
        formState: { isDirty, dirtyFields },
    } = methods;

    const serviceTestTypes = useMemo(
        () => allTestTypes.filter((testType) => !testType.hidden && testType.group === serviceGroup),
        [allTestTypes, serviceGroup]
    );

    const primaryTestType = serviceTestTypes[0];
    const allowAutomaticThresholds = !!primaryTestType?.allowAutomaticThresholds;
    const serviceGroupOptions = useMemo(() => {
        const visibleTypes = allTestTypes.filter((testType) => !testType.hidden);
        const grouped = _.groupBy(visibleTypes, 'group');

        const groupOptions = Object.entries(grouped).map(([group, types]) => {
            const firstType = _.first(types) || {};
            return {
                group,
                title:
                    types.length === 1
                        ? _.get(firstType, 'details.title') ||
                          firstType.title ||
                          firstType.name ||
                          groupTitleFallback(group)
                        : groupTitleFallback(group),
                isNativeSort: !!firstType.isNativeSort,
                isBuiltIn: firstType.builtIn === true,
            };
        });

        const sortByGroupName = (items) => _.sortBy(items, (item) => _.toLower(item.group));
        const nativeGroups = _.filter(groupOptions, (item) => item.isNativeSort);
        const builtInGroups = _.filter(groupOptions, (item) => !item.isNativeSort && item.isBuiltIn);
        const customGroups = _.filter(groupOptions, (item) => !item.isNativeSort && !item.isBuiltIn);

        return [
            ..._.reverse(sortByGroupName(nativeGroups)),
            ...sortByGroupName(builtInGroups),
            ...sortByGroupName(customGroups),
        ].map(({ group, title }) => ({ group, title }));
    }, [allTestTypes]);

    const serviceGroupDisplayTitle =
        _.find(serviceGroupOptions, { group: serviceGroup })?.title ||
        _.get(primaryTestType, 'details.title') ||
        primaryTestType?.title ||
        primaryTestType?.name ||
        serviceGroup;

    const watchedThresholds = watch('thresholds') || [];
    const automaticEnabled = useWatch({
        control,
        name: 'automaticEnabled',
        defaultValue: false,
    });
    const automaticSensitivity = useWatch({
        control,
        name: 'automaticSensitivity',
        defaultValue: 4,
    });

    const serviceMetricsById = useMemo(() => {
        const metrics = primaryTestType?.metrics || [];
        return _.keyBy(metrics, 'id');
    }, [primaryTestType]);

    const primaryMetric = useMemo(() => _.find(primaryTestType?.metrics || [], ['isMain', true]), [primaryTestType]);
    const primaryMetricId = primaryMetric?.id;
    const primaryMetricUnit = primaryMetric?.units || '';
    const hasPrimaryMetricThreshold = useMemo(() => {
        if (!primaryMetricId) {
            return false;
        }

        const referenceThresholds = _.get(serviceTestTypes, '[0].thresholds', []);
        return _.some(referenceThresholds, (threshold) => _.includes(threshold.metricIds || [], primaryMetricId));
    }, [serviceTestTypes, primaryMetricId]);

    const getServiceMetricLabel = (metricIds = []) => {
        const metric = serviceMetricsById[_.first(metricIds)];
        const metricName = metric?.name;

        if (metricName === 'lqmos_avg') {
            return t`MOS`;
        }

        return metric?.title || metricTitleFallback(metricName) || t`Metric ${_.first(metricIds)}`;
    };

    const getServiceMetricUnit = (metricIds = []) => {
        const metric = serviceMetricsById[_.first(metricIds)];
        return metric?.units || '';
    };

    const { initialized, finding, resetForm } = useFind({
        queryFunc: async () => {
            const types = await getTestTypes(dataService);
            const groupExists = _.some(types, (item) => item.group === serviceGroup);
            const effectiveGroup = groupExists ? serviceGroup : _.get(types, '[0].group', DEFAULT_SERVICE_GROUP);
            const groupedTypes = _.filter(types, (testType) => !testType.hidden && testType.group === effectiveGroup);
            const typeIds = groupedTypes.map((item) => item._id);

            const testsResponse = typeIds.length > 0 ? await getTestsByTypeIds(dataService, typeIds) : [];
            const sortedTests = _.sortBy(testsResponse, (test) => _.toLower(test.name));

            return {
                types,
                tests: sortedTests,
                effectiveGroup,
            };
        },
        postFindHandler: (response) => {
            const types = response?.types || [];
            const effectiveGroup = response?.effectiveGroup || DEFAULT_SERVICE_GROUP;
            const groupedTypes = _.filter(types, (testType) => !testType.hidden && testType.group === effectiveGroup);

            setAllTestTypes(types);
            setTests(response?.tests || []);
            setDirtyOverrides({});
            setIsSubFormDirty(false);

            if (effectiveGroup !== serviceGroup) {
                setServiceGroup(effectiveGroup);
            }

            return {
                ...buildServiceDefaults(_.first(groupedTypes)),
                [GROUP_TYPE_FIELD]: effectiveGroup,
                [OVERRIDE_TEST_IDS_FIELD]: (response?.tests || [])
                    .filter((test) => test.overrideThreshold)
                    .map((test) => test._id),
            };
        },
        resetFunc: reset,
        defaultValues: {
            [GROUP_TYPE_FIELD]: initialGroup,
            thresholds: [],
            automaticEnabled: false,
            automaticSensitivity: 4,
            [OVERRIDE_TEST_IDS_FIELD]: [],
        },
        errorHandler: errorWrapper({}),
    });

    useEffect(() => {
        setValue(GROUP_TYPE_FIELD, serviceGroup, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
    }, [serviceGroup, setValue]);

    const loadTestsForGroup = async (nextGroup) => {
        const groupedTypes = _.filter(allTestTypes, (testType) => !testType.hidden && testType.group === nextGroup);
        const typeIds = groupedTypes.map((item) => item._id);

        try {
            setFindingGroupTests(true);
            const testsResponse = typeIds.length > 0 ? await getTestsByTypeIds(dataService, typeIds) : [];
            const sortedTests = _.sortBy(testsResponse, (test) => _.toLower(test.name));

            setTests(sortedTests);
            setDirtyOverrides({});
            setIsSubFormDirty(false);
            reset({
                ...buildServiceDefaults(_.first(groupedTypes)),
                [GROUP_TYPE_FIELD]: nextGroup,
                [OVERRIDE_TEST_IDS_FIELD]: sortedTests.filter((test) => test.overrideThreshold).map((test) => test._id),
            });
        } catch (error) {
            alertsActions.createErrorAlert(error?.message || t`Failed to load service test thresholds.`);
            throw error;
        } finally {
            setFindingGroupTests(false);
        }
    };

    const handleFormSubmit = (dataToSave = {}) => {
        if (!primaryTestType) return;

        const typeIds = serviceTestTypes.map((item) => item._id);

        const payload = {
            thresholds: dataToSave.thresholds || [],
            automaticThreshold: dataToSave.automaticEnabled
                ? { sensitivity: asNumber(dataToSave.automaticSensitivity) }
                : null,
        };

        // Mirror portal/ui's saveTestThresholds: skip updateGlobalThresholds
        // when testTypes haven't changed (only overrides are dirty).
        const hasGlobalChanges =
            dirtyFields.thresholds || dirtyFields.automaticEnabled || dirtyFields.automaticSensitivity;

        return formSaveService.save({
            saveFunc: async () => {
                const thresholdValidationErrors = {};

                if (hasGlobalChanges) {
                    await updateGlobalThresholds(dataService, typeIds, payload).catch((err) => {
                        const validations =
                            _.get(err, 'responseBody.details.validations') ||
                            _.get(err, 'responseBody.details.errors', {});
                        if (_.isEmpty(validations)) throw err;
                        _.assign(thresholdValidationErrors, validations);
                    });
                }

                const overrideUpdates = Object.values(dirtyOverrides);
                await Promise.all(
                    overrideUpdates.map((test) => {
                        // portal/ui sends only 4 fields: thresholds, dynamicThresholds,
                        // thresholdsSensitivity, overrideThreshold
                        const testPayload = {
                            thresholds: test.dynamicThresholds ? [] : test.thresholds || [],
                            dynamicThresholds: !!test.dynamicThresholds,
                            thresholdsSensitivity: asNumber(
                                test.thresholdsSensitivity || dataToSave.automaticSensitivity
                            ),
                            overrideThreshold: !!test.overrideThreshold,
                        };

                        return patchTestThresholdOverride(dataService, test._id, testPayload).catch((err) => {
                            const validations =
                                _.get(err, 'responseBody.details.validations') ||
                                _.get(err, 'responseBody.details.errors', {});
                            if (_.isEmpty(validations)) throw err;
                            _.assign(thresholdValidationErrors, validations);
                        });
                    })
                );

                if (!_.isEmpty(thresholdValidationErrors)) {
                    const error = new Error('Threshold validation failed');
                    error.thresholdValidationErrors = thresholdValidationErrors;
                    throw error;
                }

                resetForm();
                setIsSubFormDirty(false);
            },
            dataToSave,
            setError,
            saveErrorHandler: handleThresholdSaveError,
        });
    };

    const handleSubmitFunction = submitWrapper(handleFormSubmit);
    const isDirtyFn = (isFormDirty) => isFormDirty || isSubFormDirty;
    const hasUnsavedChanges = () =>
        (isDirty || isSubFormDirty) && (!_.isEmpty(_.omit(dirtyFields, [GROUP_TYPE_FIELD])) || isSubFormDirty);

    const handleGroupChange = async (event) => {
        const nextGroup = event.target.value;
        if (!nextGroup || nextGroup === serviceGroup) {
            return;
        }

        if (hasUnsavedChanges()) {
            // NgpSelect updates form value before onChangeFunc, so revert when prompting.
            setValue(GROUP_TYPE_FIELD, serviceGroup, { shouldDirty: false, shouldTouch: false, shouldValidate: false });
            setChangeToGroup(nextGroup);
            setDisplayDirtyPrompt(true);
            return;
        }

        setServiceGroup(nextGroup);
        setChangeToGroup(null);
        await loadTestsForGroup(nextGroup);
        // Mark GROUP_TYPE_FIELD dirty so cross-tab navigation triggers the unsaved-changes blocker.
        setTimeout(() => setValue(GROUP_TYPE_FIELD, nextGroup, { shouldDirty: true }), 200);
    };

    const handleDirtyFormConfirm = async () => {
        const nextGroup = changeToGroup;
        setServiceGroup(nextGroup);
        setChangeToGroup(null);
        setDirtyOverrides({});
        setIsSubFormDirty(false);
        await loadTestsForGroup(nextGroup);
    };

    const handleOverrideSelectionUpdate = (selectedIds = []) => {
        const nextSelectedIds = selectedIds || [];
        const nextSelectedSet = new Set(nextSelectedIds);
        const formValues = getValues();
        const changedOverrides = {};
        let changed = false;

        setTests((prev) =>
            prev.map((test) => {
                const shouldOverride = nextSelectedSet.has(test._id);
                const isOverride = !!test.overrideThreshold;

                if (shouldOverride === isOverride) {
                    return test;
                }

                changed = true;
                const updated = shouldOverride
                    ? {
                          ...test,
                          overrideThreshold: true,
                          dynamicThresholds: !!formValues.automaticEnabled,
                          thresholdsSensitivity: asNumber(formValues.automaticSensitivity),
                          thresholds: _.cloneDeep(formValues.thresholds || []),
                      }
                    : {
                          ...test,
                          overrideThreshold: false,
                          dynamicThresholds: !!formValues.automaticEnabled,
                          thresholdsSensitivity: asNumber(formValues.automaticSensitivity),
                          thresholds: [],
                      };

                changedOverrides[updated._id] = updated;
                return updated;
            })
        );

        setValue(OVERRIDE_TEST_IDS_FIELD, nextSelectedIds, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
        });

        if (changed) {
            setDirtyOverrides((prev) => ({ ...prev, ...changedOverrides }));
            setIsSubFormDirty(true);
        }
    };

    useImperativeHandle(ref, () => ({
        save: handleSubmit(handleSubmitFunction),
    }));

    if (!initialized || finding || findingGroupTests) {
        return (
            <Box p={3} display="flex" alignItems="center" justifyContent="center">
                <CircularProgress size={32} />
            </Box>
        );
    }

    return (
        <FormProvider {...methods}>
            <NgpTwoButtonDialog
                title={dirtyFormTitleText}
                confirmButtonText={dirtyFormConfirmText}
                cancelButtonText={dirtyFormCancelText}
                open={displayDirtyPrompt}
                setOpen={setDisplayDirtyPrompt}
                onConfirm={handleDirtyFormConfirm}
                isWarning
                disableBackdropClick
                closeInTitleBar={false}
            >
                <div className={dirtyPromptClasses.formConfirmDiv}>{dirtyFormBodyText}</div>
            </NgpTwoButtonDialog>

            <NgpForm
                submitFunction={handleSubmitFunction}
                initialized={initialized}
                loading={finding || findingGroupTests || saving}
                isDirtyFunc={isDirtyFn}
                showRequiredFieldsTip={false}
            >
                <FormSection>
                    <RowFlexLayout vBottom mt={3}>
                        <Typography variant="h6">{t`Service Test Type:`}</Typography>
                        <NgpSelect
                            fieldName={GROUP_TYPE_FIELD}
                            required
                            onChangeFunc={handleGroupChange}
                            className={classes.ngpSelect}
                            selectProps={{
                                MenuProps: {
                                    getContentAnchorEl: null,
                                    anchorOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left',
                                    },
                                    transformOrigin: {
                                        vertical: 'top',
                                        horizontal: 'left',
                                    },
                                    PaperProps: {
                                        style: {
                                            maxHeight: 200,
                                        },
                                    },
                                },
                            }}
                        >
                            {serviceGroupOptions.map((option) => (
                                <MenuItem key={option.group} value={option.group}>
                                    {option.title}
                                </MenuItem>
                            ))}
                        </NgpSelect>
                    </RowFlexLayout>
                </FormSection>
                <FormSection>
                    <Typography variant="h6">{t`${serviceGroupDisplayTitle} Test Defaults`}</Typography>
                    <RowFlexLayout
                        vTop
                        className={`${classes.serviceDefaultsRow} ${
                            allowAutomaticThresholds ? classes.serviceDefaultsRowWithReservedChartSpace : ''
                        }`}
                    >
                        <Box>
                            {allowAutomaticThresholds && (
                                <AutomaticThresholdControls
                                    classes={classes}
                                    automaticEnabled={automaticEnabled}
                                    automaticSensitivity={automaticSensitivity}
                                />
                            )}

                            {watchedThresholds.map((threshold, index) => (
                                <ThresholdMetricRow
                                    key={`service-threshold-${_.join(threshold.metricIds || ['unknown'], '-')}`}
                                    threshold={threshold}
                                    index={index}
                                    getServiceMetricLabel={getServiceMetricLabel}
                                    getServiceMetricUnit={getServiceMetricUnit}
                                />
                            ))}
                        </Box>

                        {allowAutomaticThresholds && !!automaticEnabled && (
                            <div className={classes.sensitivityChartInline}>
                                <ThresholdSensitivityExampleChart sensitivity={automaticSensitivity} />
                            </div>
                        )}
                    </RowFlexLayout>
                </FormSection>

                <ThresholdsServiceOverridesSelector
                    tests={tests}
                    overrideFieldName={OVERRIDE_TEST_IDS_FIELD}
                    onSelectionUpdate={handleOverrideSelectionUpdate}
                    setTests={setTests}
                    setDirtyOverrides={setDirtyOverrides}
                    setIsSubFormDirty={setIsSubFormDirty}
                    allowAutomaticThresholds={allowAutomaticThresholds}
                    hasPrimaryMetricThreshold={hasPrimaryMetricThreshold}
                    primaryMetricId={primaryMetricId}
                    primaryMetricUnit={primaryMetricUnit}
                    getServiceMetricLabel={getServiceMetricLabel}
                    getServiceMetricUnit={getServiceMetricUnit}
                />
            </NgpForm>
        </FormProvider>
    );
});

export default ThresholdsServiceTab;
